import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';
import { Logger } from '@app/utils/logger';
import { OneSignalActions } from './one-signal.action';
import { OneSignalService } from './one-signal.service';

/**
 * One signal Effects.
 */
@Injectable()
export class OneSignalEffects extends OneSignalService {
  private readonly actions$ = inject(Actions);
  private readonly log = Logger.getInstance('OneSignalEffects');

  readonly saveNotification$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OneSignalActions.saveNotification),
      switchMap((action) =>
        this.saveNotificationClick({
          notificationId: action.notificationId
        }).pipe(
          map(() =>
            OneSignalActions.saveNotificationSuccess({ success: true })
          ),
          catchError((error: HttpErrorResponse) => {
            this.log.error(
              '[OneSignalEffects] saveNotification failed:',
              error
            );
            return of(
              OneSignalActions.saveNotificationFailed({
                error: error.message
              })
            );
          })
        )
      )
    );
  });

  /**
   * @description Effect to save notification device information when the corresponding action is dispatched.
   * It listens for the 'Save Notification Device' action, calls the saveNotificationDevice method, and handles success and error cases.
   * On success, it simply returns true.
   * On failure, it logs the error and returns false.
   * @returns {Observable<boolean>} An observable that emits true on success and false on failure.
   */
  readonly saveNotificationDevice$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OneSignalActions.saveNotificationDevice),
        switchMap((action) =>
          this.saveNotificationDevice(
            { form: action.form },
            action.context
          ).pipe(
            map(() => true),
            catchError((error: HttpErrorResponse) => {
              this.log.error(
                '[OneSignalEffects] saveNotificationDevice failed:',
                error
              );
              return of(false);
            })
          )
        )
      );
    },
    { dispatch: false }
  );
}
